// src/pages/CartellaConfirmationPage.jsx
import React, { useState, useEffect, useCallback, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axiosInstance from "../axiosInstance.js";
import PageWrapper from "../components/PageWrapper.jsx";
import { LanguageContext } from "../contexts/LanguageContext.js";
import { toast } from "react-toastify"; // ToastContainer import removed from here
// import 'react-toastify/dist/ReactToastify.css'; // This import should ideally be in your main App.js or index.js
import BraunClock from "../components/BraunClock"; // Ensure BraunClock is imported

/**
 * Helper function to determine the background color of a Bingo ball based on its number.
 * "F" (Free space) has a distinct color.
 * @param {number|string} num - The Bingo number or "F".
 * @returns {string} - Hex color code.
 */
const getBallColor = (num) => {
  if (num === "F") return "#999999";
  if (num <= 15) return "#FF3333";
  if (num <= 30) return "#3388FF";
  if (num <= 45) return "#AA33FF";
  if (num <= 60) return "#33FF66";
  return "#FFEE33";
};

/**
 * Reconstructs a 5x5 Bingo board from a 1D array of numbers.
 * It places "F" (Free space) in the center (N3 position).
 * The input 1D array represents numbers for B, I, N (excluding center), G, O columns.
 * @param {Array<number|string>} flatBoard1D - The 1D array of Bingo numbers.
 * @returns {Array<Array<number|string>>} - A 2D array representing the 5x5 Bingo board.
 */
const reconstructBoardFrom1D = (flatBoard1D) => {
  const tempGridRowMajor = Array(5)
    .fill(null)
    .map(() => Array(5).fill(null));
  const letters = ["B", "I", "N", "G", "O"];
  let currentRawIndex = 0;

  for (let rowIdx = 0; rowIdx < 5; rowIdx++) {
    for (let colIdx = 0; colIdx < 5; colIdx++) {
      // Place 'F' at the center (N3)
      if (letters[colIdx] === "N" && rowIdx === 2) {
        tempGridRowMajor[rowIdx][colIdx] = "F";
      } else {
        // Populate with numbers from the 1D array
        tempGridRowMajor[rowIdx][colIdx] =
          flatBoard1D[currentRawIndex] !== undefined
            ? flatBoard1D[currentRawIndex]
            : null;
        currentRawIndex++;
      }
    }
  }

  // Transform row-major to column-major for Bingo card display
  const playerCartellaBoard = [];
  for (let c = 0; c < 5; c++) {
    playerCartellaBoard.push([]);
    for (let r = 0; r < 5; r++) {
      playerCartellaBoard[c].push(tempGridRowMajor[c][r]);
    }
  }
  return playerCartellaBoard;
};

/**
 * CartellaPage component for displaying and confirming selected Bingo cartellas.
 * It fetches game state and cartella details, shows a countdown if the game is pending,
 * and allows the user to confirm or change their selection.
 */
const CartellaPage = () => {
  const { language } = useContext(LanguageContext);
  const navigate = useNavigate();
  const location = useLocation();

  // Extract game and cartella details from route state
  const {
    cartellaNumbers: selectedCartellaNumbers, // Array of human-readable cartella numbers
    gameId,
    stakeAmount,
    cartellaIds, // Array of backend cartella IDs
  } = location.state || {};

  // Translations object for multiple languages
  const translations = {
    en: {
      loadingCartella: "Loading cartella details for confirmation...",
      noCartellaId:
        "No cartella ID provided. Please go back and select a cartella.",
      noGameId: "No game ID provided. Please go back and select a game.",
      fetchGameStateError: "Failed to fetch game state.",
      fetchCartellaError: "Failed to fetch cartella board.",
      networkError: "Network or server error while fetching data.",
      gameCompleted:
        "This game has already completed. Redirecting to stake selection.",
      gameFailed_start:
        "This game failed to start. Redirecting to stake selection.",
      error: "Error",
      goBackToStakes: "Go Back to Stakes",
      noCartellaData: "No cartella data available for review.",
      perCard: "Birr Per Card",
      cardNumber: "Card No.",
      free: "FREE",
      goBackChange: "Go Back & Change",
      confirmPlay: "Confirm Cards & Play", // Changed for multiple cards
      waitingPlayers: "Waiting for more players to join...",
      gameReady: "Game is ready to start!",
      confirmCartellaError: "Failed to confirm cartella(s) on backend.", // Changed for multiple cards
      networkConfirmError: "Network error confirming cartella(s).", // Changed for multiple cards
      unexpectedGameStatus:
        "Confirmation successful but unexpected game status:",
      loading: "Loading...",
      selectedCards: "Selected Cards", // New translation
      insufficientBalance:
        "Insufficient balance. Please top up or reduce your stake.",
    },
    am: {
      loadingCartella: "የካርቴላ ዝርዝሮች ለማረጋገጥ በመጫን ላይ...",
      noCartellaId: "የካርቴላ መታወቂያ አልተሰጠም። እባክዎ ወደ ኋላ ተመለስ እና ካርቴላ ይምረጡ።",
      noGameId: "የጨዋታ መታወቂያ አልተሰጠም። እባክዎ ወደ ኋላ ተመለስ እና ጨዋታ ይምረጡ።",
      fetchGameStateError: "የጨዋታ ሁኔታ ማግኘት አልተቻለም።",
      fetchCartellaError: "የካርቴላ ሰሌዳ ማግኘት አልተቻለም።",
      networkError: "ዳታ በሚገኝበት ጊዜ የኔትወርክ ወይም የሰርቨር ስህተት።",
      gameCompleted: "ይህ ጨዋታ አስቀድሞ ተጠናቅቋል። ወደ ውርርድ ምርጫ በመቀየር ላይ...",
      gameFailed_start: "ይህ ጨዋታ ለመጀመር አልተቻለም። ወደ ውርርድ ምርጫ በመቀየር ላይ...",
      error: "ስህተት",
      goBackToStakes: "ወደ ውርርዶች ተመለስ",
      noCartellaData: "ለግምገማ የካርቴላ ዳታ አይገኝም።",
      perCard: " ብር በካርድ",
      cardNumber: "ካርድ ቁጥር",
      free: "ነፃ",
      goBackChange: "ተመለስ እና ቀይር",
      confirmPlay: "ካርዶችን ያረጋግጡ እና ይጫወቱ", // Changed
      waitingPlayers: "ለተጨማሪ ተጫዋቾች በመጠበቅ ላይ...",
      gameReady: "ጨዋታው ለመጀመር ዝግጁ ነው!",
      confirmCartellaError: "ካርቴላን በመገናኛ አሞሌ ላይ ማረጋገጥ አልተቻለም።",
      networkConfirmError: "ካርቴላን በሚያረጋግጥበት ጊዜ የኔትወርክ ስህተት።",
      unexpectedGameStatus: "ማረጋገጫ ተሳክቷል ነገር ግን ያልተጠበቀ የጨዋታ ሁኔታ:",
      loading: "በመጫን ላይ...",
      selectedCards: "የተመረጡ ካርዶች", // New
      insufficientBalance:
        "ምርጫዎትን መመዝገብ አልተቻለም። እባክዎ ገንዘብ ያስገቡ ወይም የውርርድ መጠንዎን ይቀንሱ።",
    },
    or: {
      loadingCartella: "Cartella details confirm gochuuf fe'amaa jira...",
      noCartellaId:
        "ID cartellaa hin kennine. Duubatti deebi'ii cartella filadhu.",
      noGameId: "ID taphaa hin kennine. Duubatti deebi'ii tapha filadhu.",
      fetchGameStateError: "Haala taphaa argachuu hin dandeenye.",
      fetchCartellaError: "Meeshaa cartellaa argachuu hin dandeenye.",
      networkError: "Daataa argachuuf dogoggora nettoworkii ykn serverii.",
      gameCompleted:
        "Tapha kana duraan xumureera. Filannoo karaa irratti deebi'uun jira...",
      gameFailed_start:
        "Tapha kana jalqabuu hin dandeenye. Filannoo karaa irratti deebi'uun jira...",
      error: "Dogoggora",
      goBackToStakes: "Karaa filannoo irratti deebi'i",
      noCartellaData: "Ilaalchaaf daataa cartellaa hin argamne.",
      perCard: " Birr Kaardii tokkoof",
      cardNumber: "Lak. Kaardii",
      free: "BILISAA",
      goBackChange: "Duubatti deebi'ii jijjiiri",
      confirmPlay: "Kaardota mirkaneessi & tapha", // Changed
      waitingPlayers: "Taphoota dabalataa eegaa jira...",
      gameReady: "Tapha jalqabuuuf qophaa'eera!",
      confirmCartellaError:
        "Cartella mirkaneessuu bakka bu'utti hin dandeenye.",
      networkConfirmError: "Cartella mirkaneessuuf dogoggora nettoworkii.",
      unexpectedGameStatus:
        "Mirkaneessichi milkeesse garuu haala taphaa hin eegamne:",
      loading: "Fe'amaa jira...",
      selectedCards: "Kaardota Filataman", // New
      insufficientBalance:
        "Maallaqa gahaa hin qabdu. Mee maallaqa dabaluu yookaan mallaqa xiqqeessi.",
    },
    ti: {
      loadingCartella: "ዝርዝር ካርቴላ ንምርግጋጽ እዚ ዝተሳተፈ እዩ...",
      noCartellaId: "ID ካርቴላ ኣይተሃበን። ብጽሒልካ ንካርቴላ ምረጽ።",
      noGameId: "ID ጸወታ ኣይተሃበን። ብጽሒልካ ንጸወታ ምረጽ።",
      fetchGameStateError: "ኩነታት ጸወታ ምርካብ ኣይተኻእለን።",
      fetchCartellaError: "ሰሌዳ ካርቴላ ምርካብ ኣይተኻእለን።",
      networkError: "ዳታ ምርካብ ናይ ኔትዎርክ ወይ ሰርቨር ጌጋ።",
      gameCompleted: "እዚ ጸወታ ቅድሚ ሕጂ ተዛዚሙ ኣሎ። ናብ ምርጫ ውርርድ እዩ ዝውሰድ...",
      gameFailed_start: "እዚ ጸወታ ንምጅማር ኣይተኻእለን። ናብ ምርጫ ውርርድ እዩ ዝውሰድ...",
      error: "ጌጋ",
      goBackToStakes: "ናብ ውርርድ ተመለስ",
      noCartellaData: "ንምፍታሽ ዳታ ካርቴላ ኣይተረኽበን።",
      perCard: "ብር ብካርድ",
      cardNumber: "ቁጽሪ ካርድ",
      free: "ናጻ",
      goBackChange: "ተመለስ እሞ ቀይር",
      confirmPlay: "ካርዶችን ኣረጋግጽ እሞ ተጻወት", // Changed
      waitingPlayers: "ንዝያዳ ተጻወትቲ እንተጸበና...",
      gameReady: "ጸወታ ንምጅማር ድሉው እዩ!",
      confirmCartellaError: "ካርቴላ ኣብ ተመን ምርግጋጽ ኣይተኻእለን።",
      networkConfirmError: "ካርቴላ ምርግጋጽ ናይ ኔትዎርክ ጌጋ።",
      unexpectedGameStatus: "ምርግጋጽ ተዓዊቱ ግን ዘይተጸበና ኩነታት ጸወታ:",
      loading: "ይጽዓን...",
      selectedCards: "ዝተመርጹ ካርዶች", // New
      insufficientBalance:
        "ኣብ ናይ ባለጌል መዝገብ ገንዘብ የቐንየለይ። እባክካ ሃብነት ውስን ወይ ገንዘብ ዝውስን ዝኾነ ኣዋጅ ወስን።",
    },
    wo: {
      loadingCartella: "Taphay qaxarettishin...",
      noCartellaId:
        "ID cartellaa hin kennine. Duubatti deebi'ii cartella filadhu.",
      noGameId: "ID taphaa hin kennine. Duubatti deebi'ii tapha filadhu.",
      networkError: "Daataa argachuuf dogoggora nettoworkii ykn serverii.",
      gameCompleted:
        "Tapha kana duraan xumureera. Filannoo karaa irratti deebi'uun jira...",
      gameFailed_start:
        "Tapha kana jalqabuu hin dandeenye. Filannoo karaa irratti deebi'uun jira...",
      error: "Dogoggora",
      goBackToStakes: "Doonaa dooranaara zaara",
      noCartellaData: "Ilaalchaaf daataa cartellaa hin argamne.",
      perCard: "Birr Kaardii tokkoof",
      cardNumber: "Lak. Kaardii",
      free: "BILISAA",
      goBackChange: "Duubatti deebi'ii jijjiiri",
      confirmPlay: "Kaardota mirkaneessi & tapha", // Changed
      waitingPlayers: "Taphoota dabalataa eegaa jira...",
      gameReady: "Tapha jalqabuuuf qophaa'eera!",
      confirmCartellaError:
        "Cartella mirkaneessuu bakka bu'utti hin dandeenye.",
      networkConfirmError: "Cartella mirkaneessuuf dogoggora nettoworkii.",
      unexpectedGameStatus:
        "Mirkaneessichi milkeesse garuu haala taphaa hin eegamne:",
      loading: "Fe'amaa jira...",
      selectedCards: "Kaardota Filataman", // New
      insufficientBalance:
        "Waldaa gahaa hin qabdu. Mee maallaqa dabali yookaan kafaltii xiqqeessi.",
    },
  };

  // Memoized translation helper function with fallback to English
  const t = useCallback(
    (key) => {
      if (translations[language] && translations[language][key] !== undefined) {
        return translations[language][key];
      }
      return translations.en[key];
    },
    [language]
  );

  // Helper to get the correct translation key for specific game statuses
  const getTranslatedGameStatusKey = useCallback((status) => {
    switch (status) {
      case "completed":
        return "gameCompleted";
      case "failed_start":
        return "gameFailed_start";
      default:
        return status;
    }
  }, []);

  // State variables for component logic and data
  // Now an array of 2D boards
  const [cartellaBoards, setCartellaBoards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [gameStatus, setGameStatus] = useState(null);
  const [gameNextDrawTime, setGameNextDrawTime] = useState(null);
  const [countdownTimer, setCountdownTimer] = useState(null);
  const [playersCount, setPlayersCount] = useState(0);

  // Use the cartellaIds from location state, which is an array of DB IDs
  const resolvedCartellaIds = cartellaIds || [];

  /**
   * Displays a toast notification with a given message and type.
   * @param {string} message - The message to display.
   * @param {string} [type='error'] - The type of toast (e.g., 'success', 'info', 'warning', 'error').
   * @param {boolean} forceDisplay - If true, displays toast even in production.
   */
  const showToast = useCallback(
    (message, type = "error", forceDisplay = false) => {
      // In production, only show if forceDisplay is true
      if (process.env.NODE_ENV === "production" && !forceDisplay) {
        return;
      }

      // Use toast directly with consistent options
      toast[type](message, {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    },
    []
  );

  /**
   * Calculates the remaining time in seconds until a target draw time.
   * @param {Date|null} drawTime - The target Date object.
   * @returns {number} - Remaining time in seconds.
   */
  const calculateCountdown = useCallback((drawTime) => {
    if (!drawTime) return null;
    const now = new Date().getTime();
    const targetTime = new Date(drawTime).getTime();
    const timeLeftMs = targetTime - now;
    if (timeLeftMs <= 0) return 0;
    return Math.floor(timeLeftMs / 1000);
  }, []);

  /**
   * Fetches initial game state and cartella board details on component mount.
   * Handles redirection based on game status.
   */
  useEffect(() => {
    const fetchInitialData = async () => {
      // Validate necessary IDs from route state
      if (!resolvedCartellaIds || resolvedCartellaIds.length === 0) {
        showToast(t("noCartellaId"), "error", true); // Critical
        setLoading(false);
        return;
      }
      if (!gameId) {
        showToast(t("noGameId"), "error", true); // Critical
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        // Fetch game state
        const gameStateResponse = await axiosInstance.get(
          `/game/${gameId}/state`
        );
        if (gameStateResponse.data.success && gameStateResponse.data.game) {
          const game = gameStateResponse.data.game;
          setGameStatus(game.status);
          setGameNextDrawTime(
            game.next_draw_time ? new Date(game.next_draw_time) : null
          );
          setPlayersCount(game.total_players || 0);

          // Redirect if game is already active or ended
          if (game.status === "active") {
            navigate("/play-cartella", {
              state: {
                gameId: gameId,
                cartellaIds: resolvedCartellaIds, // Pass array of IDs
                cartellaNumbers: selectedCartellaNumbers, // Pass array of numbers
                stakeAmount: stakeAmount,
              },
            });
            return; // Stop further execution
          } else if (
            game.status === "completed" ||
            game.status === "failed_start"
          ) {
            showToast(t(getTranslatedGameStatusKey(game.status)), "info", true); // Critical game outcome
            setTimeout(() => navigate("/stake-selection"), 2000);
            return; // Stop further execution
          }
        } else {
          // Throw error if game state fetch was unsuccessful
          throw new Error(
            gameStateResponse.data.message || t("fetchGameStateError")
          );
        }

        // Fetch details for each selected cartella
        const fetchedBoards = [];
        for (const cId of resolvedCartellaIds) {
          const boardResponse = await axiosInstance.get(
            `/game/cartella-board-details/${cId}`
          );
          if (boardResponse.data.success && boardResponse.data.cartella) {
            fetchedBoards.push({
              id: cId,
              number: cId, // Assuming cartellaId is also the human-readable number for now
              board: reconstructBoardFrom1D(boardResponse.data.cartella),
            });
          }
        }
        setCartellaBoards(fetchedBoards);
      } catch (err) {
        if (err.response?.status === 401 || err.response?.status === 403) {
          localStorage.removeItem("token"); // Clear invalid token
          navigate("/login"); // Redirect to login
        }
      } finally {
        setLoading(false); // Always set loading to false
      }
    };

    fetchInitialData();
  }, [
    resolvedCartellaIds,
    gameId,
    navigate,
    stakeAmount,
    selectedCartellaNumbers,
    t,
    getTranslatedGameStatusKey,
    showToast,
  ]);

  /**
   * Manages the countdown timer for pending games.
   * Fetches updated game state when countdown reaches zero.
   */
  useEffect(() => {
    let timerInterval;

    if (gameStatus === "pending" && gameNextDrawTime) {
      timerInterval = setInterval(async () => {
        const currentCountdown = calculateCountdown(gameNextDrawTime);
        setCountdownTimer(currentCountdown);

        if (currentCountdown <= 0) {
          clearInterval(timerInterval);
          timerInterval = null; // Clear interval reference

          try {
            // Re-fetch game state when countdown ends
            const gameStateResponse = await axiosInstance.get(
              `/game/${gameId}/state`
            );
            if (gameStateResponse.data.success && gameStateResponse.data.game) {
              const game = gameStateResponse.data.game;
              setGameStatus(game.status);
              setPlayersCount(game.total_players || 0);

              // Redirect based on updated game status
              if (game.status === "active") {
                navigate("/play-cartella", {
                  state: {
                    gameId: gameId,
                    cartellaIds: resolvedCartellaIds,
                    cartellaNumbers: selectedCartellaNumbers,
                    stakeAmount: stakeAmount,
                  },
                });
              } else if (game.status === "pending" && game.total_players < 2) {
                showToast(t("gameFailed_start"), "info", true); // Critical game outcome
                setTimeout(() => navigate("/stake-selection"), 2000);
              } else if (
                game.status === "completed" ||
                game.status === "failed_start"
              ) {
                showToast(
                  t(getTranslatedGameStatusKey(game.status)),
                  "info",
                  true
                ); // Critical game outcome
                setTimeout(() => navigate("/stake-selection"), 2000);
              }
            } else {
              setTimeout(() => navigate("/stake-selection"), 2000);
            }
          } catch (err) {
            setTimeout(() => navigate("/stake-selection"), 2000);
          }
        }
      }, 1000); // Update every second
    }

    // Cleanup interval on component unmount or dependency change
    return () => {
      if (timerInterval) {
        clearInterval(timerInterval);
      }
    };
  }, [
    gameStatus,
    gameNextDrawTime,
    gameId,
    resolvedCartellaIds,
    stakeAmount,
    navigate,
    selectedCartellaNumbers,
    t,
    calculateCountdown,
    getTranslatedGameStatusKey,
    showToast,
  ]);

  /**
   * Handles the confirmation of the Bingo cartella(s) with the backend.
   */
  const handleConfirm = async () => {
    try {
      setLoading(true); // Set loading state during confirmation

      // Send the array of cartella IDs for confirmation
      const response = await axiosInstance.post(
        `/game/${gameId}/confirm-cartella`,
        {
          cartellaIds: resolvedCartellaIds, // Send array
        }
      );

      if (response.data.success) {
        const newGameStatus = response.data.gameStatus;

        if (newGameStatus === "active") {
          // Game becomes active → go to play page
          navigate("/play-cartella", {
            state: {
              gameId,
              cartellaIds: resolvedCartellaIds,
              cartellaNumbers: selectedCartellaNumbers,
              stakeAmount,
            },
          });
        } else if (newGameStatus === "pending") {
          // Game is pending → go back to lobby
          showToast(
            "Cartella(s) confirmed! Waiting for game to start.",
            "success",
            true
          );
          navigate("/bingo-game", {
            state: {
              gameId,
              stakeAmount,
              cartellaIds: resolvedCartellaIds,
              cartellaNumbers: selectedCartellaNumbers,
            },
          });
        } else if (
          newGameStatus === "completed" ||
          newGameStatus === "failed_start"
        ) {
          // Completed or failed game
          showToast(t(getTranslatedGameStatusKey(newGameStatus)), "info", true);
          setTimeout(() => navigate("/stake-selection"), 2000);
        } else {
          // Unexpected game status
          showToast(
            `${t("unexpectedGameStatus")} ${newGameStatus}.`,
            "error",
            true
          );
        }
      }
    } catch (err) {
      const message = err.response?.data?.message || ""; // Extract message from error response
      const statusCode = err.response?.status; // Extract status code
      if (statusCode === 401 || statusCode === 403) {
        localStorage.removeItem("token");
        navigate("/login");
      } else if (
        statusCode === 400 &&
        typeof message === "string" &&
        message.toLowerCase().includes("insufficient")
      ) {
        showToast(t("insufficientBalance"), "error", true);
        setTimeout(() => {
          navigate("/wallet"); // Redirect to wallet page
        }, 1000);
      } else {
        // Generic error message if specific conditions are not met
        showToast(
          t("confirmCartellaError") || "Failed to confirm cartella.",
          "error",
          true
        );
      }
    } finally {
      setLoading(false); // Always reset loading state
    }
  };
  // Render loading state
  if (loading) {
    return (
      <PageWrapper backgroundClass="bg-blue-900 text-white flex items-center justify-center">
        <p className="text-xl">{t("loadingCartella")}</p>
        {/* Removed ToastContainer from here */}
      </PageWrapper>
    );
  }

  // Render error state if no cartella data is available
  if (!cartellaBoards || cartellaBoards.length === 0) {
    return (
      <PageWrapper backgroundClass="bg-blue-900 text-white flex flex-col items-center justify-center">
        <p className="text-xl">{t("noCartellaData")}</p>
        <button
          onClick={() => navigate("/stake-selection")}
          className="mt-4 bg-purple-900 px-4 py-2 rounded"
        >
          {t("goBackToStakes")}
        </button>
        {/* Removed ToastContainer from here */}
      </PageWrapper>
    );
  }

  return (
    <PageWrapper backgroundClass="bg-blue-900 text-white min-h-screen flex flex-col items-center justify-center">
      {/* Removed ToastContainer from here. It should be in your App.js or index.js */}
      {/* Pass countdownTimer and gameStatus to BraunClock */}
      <BraunClock countdown={countdownTimer} gameStatus={gameStatus} />
      {/* Stake amount display */}
      <div className="w-full flex justify-center items-center px-4 pt-4">
        <div className="bg-gradient-to-b from-gray-300 to-gray-500 rounded-full px-4 py-2 text-sm sm:text-base text-gray-800 font-bold shadow-md">
          {stakeAmount !== null
            ? `${Number(stakeAmount).toFixed(2)}`
            : t("loading")}{" "}
          {t("perCard")}
        </div>
      </div>
      {/* Bingo cartella(s) display */}
      <div className="mt-6 p-4 bg-blue-800 bg-opacity-70 rounded-xl text-center shadow-lg border border-blue-700 max-w-full w-full overflow-x-auto">
        <h2 className="text-2xl sm:text-3xl font-bold mb-4 text-yellow-300">
          {t("selectedCards")}
        </h2>
        <div className="flex flex-wrap justify-center gap-4">
          {" "}
          {/* Container for multiple cards */}
          {cartellaBoards.map((cartellaData) => (
            <div
              key={cartellaData.id}
              className="flex flex-col items-center mb-4"
            >
              <h3 className="text-xl sm:text-2xl font-bold mb-2 text-yellow-200">
                {t("cardNumber")} {cartellaData.number}
              </h3>
              <div
                className="grid grid-cols-5 gap-0.5 bg-gray-200 border-2 border-gray-400 p-0.5 rounded-lg mx-auto font-digital"
                style={{ maxWidth: "280px", width: "90%" }}
              >
                {/* Bingo letters header (B, I, N, G, O) */}
                {["B", "I", "N", "G", "O"].map((letter, colIdx) => (
                  <div
                    key={letter}
                    className="w-full h-auto aspect-square flex items-center justify-center
                                             text-lg sm:text-xl font-extrabold text-white bg-red-700 shadow-md uppercase"
                    style={{ border: "1px solid #a00" }}
                  >
                    {letter}
                  </div>
                ))}
                {/* Render individual Bingo cells */}
                {cartellaData.board.flat().map(
                  (
                    cell,
                    idx // Flatten the 2D board for mapping
                  ) => (
                    <div
                      key={`${cartellaData.id}-${idx}`}
                      className={`w-full h-auto aspect-square flex items-center justify-center
                                         rounded-sm font-bold text-lg sm:text-xl border border-gray-400 shadow-inner
                                         ${
                                           cell === "F"
                                             ? "bg-gray-500 text-white"
                                             : "bg-white text-gray-800"
                                         }`}
                    >
                      {cell === "F" ? t("free") : cell}
                    </div>
                  )
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Action buttons: Go Back & Change, Confirm Cards & Play */}
        <div className="flex justify-center gap-4 mt-6">
          <button
            onClick={() =>
              navigate("/bingo-game", {
                state: {
                  gameId: gameId,
                  // Pass back the array of selected cartella IDs and numbers
                  cartellaIds: resolvedCartellaIds,
                  cartellaNumbers: selectedCartellaNumbers,
                  stakeAmount: stakeAmount,
                },
              })
            }
            className="bg-purple-900 hover:bg-purple-800 text-white font-bold py-2 px-6 rounded-lg shadow-lg transition-colors transform hover:scale-105"
          >
            {t("goBackChange")}
          </button>
          <button
            onClick={handleConfirm}
            className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-6 rounded-lg shadow-lg transition-colors transform hover:scale-105"
          >
            {t("confirmPlay")}
          </button>
        </div>
      </div>
      {/* Game status display (waiting for players / game ready) */}
      <div className="mt-8 text-center bg-blue-800 bg-opacity-70 p-4 rounded-xl shadow-lg border border-blue-700">
        {gameStatus === "pending" && playersCount < 2 && (
          <p className="text-xl text-yellow-300 mb-2">{t("waitingPlayers")}</p>
        )}
        {gameStatus === "pending" && playersCount >= 2 && (
          <p className="text-xl text-green-400 mb-2">{t("gameReady")}</p>
        )}
      </div>
    </PageWrapper>
  );
};

export default CartellaPage;
